import { hostBrand } from "@/lib/brand";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MorrMark } from "@/components/MorrMark";
import { AppSwitcher } from "@/components/mc/AppSwitcher";
import { BusinessSwitcher } from "@/components/mc/BusinessSwitcher";

export function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className="bg-card border-r border-b border-border p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`mt-2 font-mono font-bold text-3xl ${accent ? "text-primary" : "text-foreground"}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Shell({ title, badge, right, children, onLogout, extra, brandName }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background sticky top-0 z-30">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <MorrMark size={24} />
              <span className="font-display font-extrabold tracking-tighter" data-testid="brand-wordmark">{brandName || hostBrand().name}</span>
            </Link>
            {badge && <span className="font-mono text-[10px] uppercase tracking-wider bg-secondary border border-border px-2 py-0.5">{badge}</span>}
          </div>
          <div className="flex items-center gap-2">
            <BusinessSwitcher />
            <AppSwitcher />
            {right}
            {onLogout && (
              <Button variant="ghost" size="sm" onClick={onLogout} className="rounded-none gap-2" data-testid="logout-btn">
                <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
              </Button>
            )}
          </div>
        </div>
        {extra}
      </header>
      <main className="px-4 sm:px-6 py-6 max-w-[1500px] mx-auto">
        {title && <h1 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl mb-6">{title}</h1>}
        {children}
      </main>
    </div>
  );
}

export function money(n) {
  return "$" + Number(n || 0).toFixed(2);
}

export function timeStr(iso, tz) {
  if (!iso) return "—";
  try {
    const opts = { hour: "numeric", minute: "2-digit" };
    if (tz) opts.timeZone = tz;
    return new Date(iso).toLocaleTimeString([], opts);
  } catch { return "—"; }
}

export function dateStr(iso, tz) {
  if (!iso) return "—";
  try {
    const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    if (tz) opts.timeZone = tz;
    return new Date(iso).toLocaleString([], opts);
  } catch { return "—"; }
}

// Event bookings store date as "YYYY-MM-DD" and time as 24h "HH:MM".
export function fmtEvDate(date) {
  if (!date) return "";
  try {
    return new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return date; }
}

export function fmtEvTime(time) {
  if (!time) return "";
  try {
    return new Date("2000-01-01T" + time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return time; }
}

// "Friday, July 31, 2026 at 6:00 PM (America/Phoenix)"
export function fmtEvWhen(date, time, tzName) {
  const parts = [];
  if (date) parts.push(fmtEvDate(date));
  if (time) parts.push((parts.length ? "at " : "") + fmtEvTime(time));
  let s = parts.join(" ");
  if (s && tzName) s += ` (${tzName})`;
  return s;
}
